/* eslint-disable prettier/prettier */
import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { useAuth } from '../../context/AuthContext';
import { UserType } from '../../types/UserType';
import { UserService } from '../../services/user.service';
import { UserTableHeader } from '../../components/user/UserTableHeader';
import { UserRow } from '../../components/user/UserRow';
import { UserModal } from '../../components/user/UserModal';

export const UsersPage = () => {
  const [users, setUsers] = useState<UserType[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserType | null>(null);
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      const res = await UserService.getUsers();
      setUsers(res.data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingUser(null);
  };

  const changeStatus = async (user: any) => {
    try {
      const res = await UserService.toggleStatus(user.id);
      setUsers((prev) => prev.map((u) => (u.id === user.id ? res.data : u)));
      loadUsers();
    } catch (err) {
      console.error('Failed to toggle user status:', err);
    }
  };

  return (
    <div>
      <h1 className="text-4xl font-bold text-center sm:pb-4 sm:text-left text-primary">Users</h1>

      <div className="flex justify-center sm:justify-start">
        <Button
          icon={<Plus />}
          className="px-16 py-3 mt-4 sm:px-6 sm:mt-0"
          onClick={() => {
            setEditingUser(null);
            setIsModalOpen(true);
          }}
        >
          New
        </Button>
      </div>

      <div className="mt-8">
        {isLoading ? (
          <p className="mt-6 text-xl text-center text-muted-foreground text-primary">Loading...</p>
        ) : users.length === 0 ? (
          <p className="mt-6 text-xl text-center text-primary text-muted-foreground">
            No users found.
          </p>
        ) : (
          <>
            <UserTableHeader isSuperAdmin={user?.role?.name === 'SUPER_ADMIN'} />
            {users.map((u) => (
              <UserRow
                key={u.id}
                user={u}
                onView={() => {
                  setEditingUser(u);
                  setIsModalOpen(true);
                }}
                isSuperAdmin={user?.role?.name === 'SUPER_ADMIN'}
                onToggleStatus={() => changeStatus(u)}
                currentUserId={user?.id}
              />
            ))}
          </>
        )}
      </div>

      <UserModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSuccess={() => {
          loadUsers();
          handleModalClose();
        }}
        editingUser={editingUser}
        lockedCompanyId={user?.role?.name === 'COMPANY_ADMIN' ? user?.company?.id : undefined}
      />
    </div>
  );
};
